/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { AssetHistory, LocationSample } from './types'

/** How far a GPS fix after `time` can be and still describe the asset's position. */
const ADDRESS_WINDOW_MS = 15 * 60000

/**
 * The address at `time`: the nearest location sample when one lies within 15 minutes,
 * otherwise the last sample before `time`, however old. A parked device stops sending
 * locations until it moves again (MEGA-737: hours of temperature readings, no fixes), so
 * the last fix is still where the asset is. Samples arrive sorted by time, so this is a
 * binary search.
 */
export const addressLookup = (locations: LocationSample[]) => {
  const samples = locations.map((loc) => ({ t: new Date(loc.time).getTime(), address: loc.address }))
  return (time: string | number): string | undefined => {
    if (!samples.length) return undefined
    const t = typeof time === 'number' ? time : new Date(time).getTime()
    // lo is the first sample at or after t (or the last sample when every one is before t).
    let lo = 0
    let hi = samples.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (samples[mid].t < t) lo = mid + 1
      else hi = mid
    }
    // The closest candidate is lo or the sample before it.
    let best = lo
    if (lo > 0 && Math.abs(samples[lo - 1].t - t) < Math.abs(samples[lo].t - t)) {
      best = lo - 1
    }
    if (Math.abs(samples[best].t - t) <= ADDRESS_WINDOW_MS) return samples[best].address
    const previous = samples[lo].t <= t ? lo : lo - 1
    return previous >= 0 ? samples[previous].address : undefined
  }
}

export interface CursorInfo {
  address?: string
  /** Every geofence the asset was inside at the time; more than one where geofences nest. */
  geofences: string[]
}

/**
 * What the cursor popover says about an instant: the nearest address and the geofences the
 * asset was inside. Geofences come from the visit log; an API without it (before MEGA-737)
 * falls back to the geofence tagged on the nearest location sample.
 */
export const cursorInfoLookup = (data: AssetHistory, hideGeofences: boolean) => {
  const addressAt = addressLookup(data.location ?? [])
  const visits = (data.geofences ?? []).map((visit) => ({
    name: visit.name,
    entry: new Date(visit.entryTime).getTime(),
    exit: visit.exitTime ? new Date(visit.exitTime).getTime() : Infinity,
  }))
  const fallbackGeofenceAt = (time: number): string[] => {
    const nearest = (data.location ?? []).reduce<LocationSample | undefined>((best, loc) => {
      const d = Math.abs(new Date(loc.time).getTime() - time)
      return !best || d < Math.abs(new Date(best.time).getTime() - time) ? loc : best
    }, undefined)
    return nearest?.geofence?.name ? [nearest.geofence.name] : []
  }

  return (time: number): CursorInfo => {
    let geofences: string[] = []
    if (!hideGeofences) {
      geofences = data.geofences
        ? [...new Set(visits.filter((v) => time >= v.entry && time <= v.exit).map((v) => v.name))]
        : fallbackGeofenceAt(time)
    }
    return { address: addressAt(time), geofences }
  }
}
